/**
 * Phase H9 — Admissions invalid-URL guard.
 *
 * The IA app's admissions section uses a parametric route
 * `/admissions/:enquiryId` for the enquiry detail page. Any sibling
 * sub-path the user types — e.g. `/admissions/configure`,
 * `/admissions/foo`, or a stale Slack link — gets caught by the
 * `:enquiryId` route and fired straight at the BE, which then rejects
 * the request because the value isn't a valid ObjectId.
 *
 * Pre-Phase-H9 the page rendered a generic
 * "Couldn't load enquiry. Try refreshing — if it keeps happening,
 * check your network." card. That copy is misleading: refreshing does
 * nothing, the URL is just malformed.
 *
 * This module provides a cheap, dependency-free check that returns
 * `true` only for strings shaped like a canonical Mongo ObjectId
 * (24 hex chars, case-insensitive). EnquiryDetailPage uses it as a
 * post-`useParams` gate so it can render a tailored "Invalid enquiry
 * URL" state and skip the doomed BE call entirely.
 *
 * Implementation notes:
 *   - We don't import a Mongo-specific library; an anchored regex is
 *     the same cost and avoids pulling a server-side dependency into
 *     the browser bundle.
 *   - Mongo accepts upper-, lower-, and mixed-case hex, so the regex
 *     is case-insensitive.
 *   - We do NOT trim. The route param is decoded by react-router
 *     before reaching us; whitespace inside the value means the URL
 *     was hand-edited and should be treated as invalid (we want the
 *     "Invalid enquiry URL" state, not a silent best-effort match).
 */
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

export function isValidEnquiryId(candidate: string): boolean {
  return OBJECT_ID_PATTERN.test(candidate);
}
